import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/ui/icons';
import { Badge } from '@/components/ui/badge';
import { VerificationStatusBadge } from '@/components/features/verification/VerificationStatusBadge';
import { useKeysetPagination } from '@/hooks/use-keyset-pagination';
import type { Id } from 'convex/_generated/dataModel';
import { type VerificationStatus } from '@/types/validators';
import { formatBerlinDate } from '@/lib/berlin-time';
import { type VerificationTab } from '@/types/verification';

const OperatorVerificationDashboard = () => {
  const { t, i18n } = useTranslation('operator');
  const [activeTab, setActiveTab] = useState<VerificationTab>('pending');

  // DA-362: Use keyset pagination hook (fixes cursor incompatibility bug)
  const { currentKeyset, goToNextPage, goToPrevPage, resetPagination, currentPage, hasPrevPage } = useKeysetPagination<Id<'userProfiles'>>();

  // DA-362: Reset pagination when tab changes (critical for preventing stale keyset errors)
  useEffect(() => {
    resetPagination();
  }, [activeTab, resetPagination]);

  // DA-362: Use new keyset-paginated query for all tabs except 'expiring'
  // Build args object conditionally to handle exactOptionalPropertyTypes
  // Note: Backend query doesn't support 'unapproved' yet, so we exclude it from tabs
  const keysetArgs =
    activeTab !== 'expiring' && activeTab !== 'unapproved'
      ? ({
          status: activeTab as Exclude<VerificationStatus, 'unapproved'>,
          pageSize: 50,
          includeTotal: true,
          ...(currentKeyset.lastCreationTime !== null && currentKeyset.lastId !== null ? { lastCreationTime: currentKeyset.lastCreationTime, lastId: currentKeyset.lastId } : {}),
        } as const)
      : ('skip' as const);

  const keysetData = useQuery(api.app.verificationOperator.listPendingVerificationKeysetPaginated, keysetArgs);

  // Query expiring insurance (unchanged)
  const expiringData = useQuery(api.app.verificationOperator.listExpiringInsurance, activeTab === 'expiring' ? { daysThreshold: 30 } : 'skip');

  // Get current data based on active tab
  const getCurrentData = () => {
    if (activeTab === 'expiring') {
      return { page: expiringData || [], hasNextPage: false, total: expiringData?.length || 0 };
    }
    return keysetData;
  };

  const data = getCurrentData();
  const items = data && 'page' in data ? data.page : [];

  const formatDate = (timestamp: number | undefined) => {
    if (!timestamp) return '—';
    return formatBerlinDate(timestamp, i18n.language || 'de-DE');
  };

  const tabs: Array<{ key: VerificationTab; label: string; count?: number | undefined }> = [
    { key: 'pending', label: t('verification.verificationDashboard.tabs.pendingReview'), count: activeTab === 'pending' ? keysetData?.total : undefined },
    {
      key: 'changes_requested',
      label: t('verification.verificationDashboard.tabs.changesRequested'),
      count: activeTab === 'changes_requested' ? keysetData?.total : undefined,
    },
    { key: 'expiring', label: t('verification.verificationDashboard.tabs.expiringSoon'), count: expiringData?.length },
    {
      key: 'expired_insurance',
      label: t('verification.verificationDashboard.tabs.expired'),
      count: activeTab === 'expired_insurance' ? keysetData?.total : undefined,
    },
    { key: 'verified', label: t('verification.verificationDashboard.tabs.allVerified'), count: activeTab === 'verified' ? keysetData?.total : undefined },
  ];

  return (
    <div className='p-4 sm:p-6 lg:p-8 space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-semibold'>{t('verification.verificationDashboard.title')}</h1>
          <p className='text-sm text-muted-foreground mt-1'>{t('verification.verificationDashboard.description')}</p>
        </div>
      </div>

      {/* Tabs */}
      <Card>
        <CardHeader className='pb-3'>
          <div className='flex flex-wrap gap-2'>
            {tabs.map((tab) => (
              <Button key={tab.key} variant={activeTab === tab.key ? 'default' : 'outline'} size='sm' onClick={() => setActiveTab(tab.key)}>
                {tab.label}
                {typeof tab.count === 'number' && tab.count > 0 && (
                  <Badge variant='secondary' className='ml-2'>
                    {tab.count}
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        </CardHeader>

        <CardContent>
          {!data && (
            <div className='flex items-center justify-center py-8'>
              <Icons.Loader className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          )}

          {data && items.length === 0 && (
            <div className='text-center py-8 text-muted-foreground'>
              <Icons.CheckCircle className='h-12 w-12 mx-auto mb-3 opacity-50' />
              <p className='text-sm'>{t('verification.verificationDashboard.empty')}</p>
            </div>
          )}

          {data && items.length > 0 && (
            <div className='border rounded-lg overflow-hidden'>
              <table className='w-full text-sm'>
                <thead className='bg-muted/30'>
                  <tr>
                    <th className='text-left px-4 py-3 font-medium'>{t('verification.verificationDashboard.table.name')}</th>
                    <th className='text-left px-4 py-3 font-medium'>{t('verification.verificationDashboard.table.email')}</th>
                    {activeTab === 'expiring' ? (
                      <>
                        <th className='text-left px-4 py-3 font-medium'>{t('verification.verificationDashboard.table.insuranceCompany')}</th>
                        <th className='text-left px-4 py-3 font-medium'>{t('verification.verificationDashboard.table.expires')}</th>
                        <th className='text-left px-4 py-3 font-medium'>{t('verification.verificationDashboard.table.daysLeft')}</th>
                      </>
                    ) : (
                      <>
                        <th className='text-left px-4 py-3 font-medium'>{t('verification.verificationDashboard.table.specialization')}</th>
                        <th className='text-left px-4 py-3 font-medium'>{t('verification.verificationDashboard.table.submitted')}</th>
                        <th className='text-left px-4 py-3 font-medium'>{t('verification.verificationDashboard.table.status')}</th>
                        <th className='text-left px-4 py-3 font-medium'>{t('verification.verificationDashboard.table.documents')}</th>
                      </>
                    )}
                    <th className='text-right px-4 py-3 font-medium'>{t('verification.verificationDashboard.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: any) => {
                    const isExpiringView = activeTab === 'expiring';

                    return (
                      <tr key={item.userId} className='border-t hover:bg-muted/20'>
                        <td className='px-4 py-3'>{item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : '—'}</td>
                        <td className='px-4 py-3 text-muted-foreground'>{item.email}</td>
                        {isExpiringView ? (
                          <>
                            <td className='px-4 py-3'>{item.insuranceCompany || '—'}</td>
                            <td className='px-4 py-3'>{formatDate(item.expiresAt)}</td>
                            <td className='px-4 py-3'>
                              <span className={item.daysUntilExpiration <= 7 ? 'text-destructive font-semibold' : item.daysUntilExpiration <= 14 ? 'text-warning font-medium' : ''}>{item.daysUntilExpiration}</span>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className='px-4 py-3'>
                              {item.specialization?.slice(0, 2).join(', ') || '—'}
                              {item.specialization?.length > 2 && ` +${item.specialization.length - 2}`}
                            </td>
                            <td className='px-4 py-3'>{formatDate(item.submittedAt)}</td>
                            <td className='px-4 py-3'>
                              <VerificationStatusBadge status={item.verificationStatus} />
                            </td>
                            <td className='px-4 py-3'>
                              <div className='flex gap-1'>
                                {item.hasApprobation && (
                                  <Badge variant='outline' className='text-xs'>
                                    {t('documents.fields.approbation')}
                                  </Badge>
                                )}
                                {item.hasInsurance && (
                                  <Badge variant='outline' className='text-xs'>
                                    {t('documents.insurance')}
                                  </Badge>
                                )}
                              </div>
                            </td>
                          </>
                        )}
                        <td className='px-4 py-3 text-right'>
                          <Button size='sm' variant='outline' render={<a href={`/operators/users/${encodeURIComponent(String(item.userId))}?tab=documents`} />}>
                            <Icons.Eye className='h-4 w-4 mr-1' />
                            {t('verification.verificationDashboard.table.review')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* DA-362: Keyset pagination controls */}
          {data && activeTab !== 'expiring' && (items.length > 0 || currentPage > 1) && (
            <div className='flex items-center justify-between mt-4 pt-4 border-t'>
              <div className='text-sm text-muted-foreground'>
                {t('verification.verificationDashboard.pagination.page', { page: currentPage })}
                {data && 'total' in data && data.total ? ` ${t('verification.verificationDashboard.pagination.of', { totalPages: Math.ceil(data.total / 50) })}` : ''}
                {data && 'total' in data && data.total ? ` • ${t('verification.verificationDashboard.pagination.total', { total: data.total })}` : ''}
              </div>
              <div className='flex gap-2'>
                <Button size='sm' variant='outline' onClick={goToPrevPage} disabled={!hasPrevPage}>
                  <Icons.ChevronLeft className='h-4 w-4 mr-1' />
                  {t('verification.verificationDashboard.pagination.previous')}
                </Button>
                <Button size='sm' variant='outline' onClick={() => data && 'nextKeyset' in data && goToNextPage(data.nextKeyset)} disabled={!data || !('hasNextPage' in data) || !data.hasNextPage}>
                  {t('verification.verificationDashboard.pagination.next')}
                  <Icons.ChevronRight className='h-4 w-4 ml-1' />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/verification/')({
  loader: async () => {
    const { default: i18n } = await import('../i18n');
    await i18n.loadNamespaces?.(['operator']);
    return null;
  },
  component: OperatorVerificationDashboard,
});
